<script>
	import { onMount } from 'svelte';
	import {
		signInWithGoogle,
		signOut,
		sendMagicLink,
		isSignInWithEmailLink,
		completeMagicLink,
		isAuthenticated,
		authState,
	} from '../lib/auth.js';

	export let navigate = (p) => { window.location.pathname = p; };

	// ---------------------------------------------------------------------------
	// P4 — MCP OAuth handoff & magic-link callback detection
	// ---------------------------------------------------------------------------
	//
	// MCP mode: the Mind MCP authorize handler redirected the user here with
	// `mcp_state` (signed OAuth bag) + `mcp_complete_url` (Mind's callback).
	// After Firebase sign-in, instead of navigating to /explorer we POST the
	// id_token + mcp_state back to mcp_complete_url so Mind can mint the OAuth
	// code and redirect to Claude Code's localhost callback.
	//
	// Magic-link complete mode: the user clicked the email magic link and
	// landed back here (typically at /sign-in/complete). We finish the
	// signInWithEmailLink handshake; onAuthStateChanged then fires.

	function readQueryParams() {
		const p = new URLSearchParams(window.location.search);
		return {
			mcp_state: p.get('mcp_state'),
			mcp_complete_url: p.get('mcp_complete_url'),
		};
	}

	let mcpParams = readQueryParams();
	$: mcpMode = !!mcpParams.mcp_state && !!mcpParams.mcp_complete_url;
	let mcpHandoffSubmitting = false;
	let magicLinkCompleting = false;
	let magicLinkError = '';

	// MCP-mode confirmation gate. When the user lands on /sign-in?mcp_state=...
	// already authenticated (Firebase session cached in browser), we MUST NOT
	// silently complete the OAuth handoff — that locks them into whatever
	// account the browser happens to be signed into without giving them a
	// chance to switch tenants. Instead, show a confirmation card with their
	// current identity and a "use a different account" escape hatch.
	let confirmedHandoff = false;
	let switchingAccount = false;

	const SHAPES = ['triangle', 'diamond', 'hexagon', 'square', 'cross', 'pentagon', 'star', 'octagon', 'circle'];
	const MARK_TYPES = ['crosshair', 'ring', 'tick', 'point'];

	// 'hero' -> landed (default for visitors) | 'splash' -> shapes appear (post-login) | 'transition' -> morph
	let phase = 'hero';
	let step = -1;
	let nodeShapes = [SHAPES[0], SHAPES[1], SHAPES[2]];
	let marks = [];
	let showLogin = false;
	let tids = [];
	let markIv;

	function later(fn, ms) {
		tids.push(setTimeout(fn, ms));
	}

	function pickRandom() {
		return SHAPES[Math.floor(Math.random() * SHAPES.length)];
	}

	// -- Splash sequence (runs once) --

	function runSplash() {
		nodeShapes = [pickRandom(), pickRandom(), pickRandom()];

		later(() => (step = 0), 100);
		later(() => (step = 1), 400);
		later(() => (step = 2), 700);
		later(() => {
			phase = 'transition';
			clearInterval(markIv);
		}, 1200);
		later(() => {
			phase = 'hero';
		}, 1800);
	}

	// -- Shape SVG paths --

	function shapePath(type) {
		const s = 9;
		switch (type) {
			case 'triangle': {
				const h = s * 0.866;
				return `M0 ${-s} L${h} ${s * 0.5} L${-h} ${s * 0.5} Z`;
			}
			case 'diamond':
				return `M0 ${-s} L${s * 0.7} 0 L0 ${s} L${-s * 0.7} 0 Z`;
			case 'hexagon': {
				const pts = Array.from({ length: 6 }, (_, i) => {
					const a = (Math.PI / 3) * i - Math.PI / 2;
					return `${(s * Math.cos(a)).toFixed(2)} ${(s * Math.sin(a)).toFixed(2)}`;
				});
				return `M${pts.join(' L')} Z`;
			}
			case 'square': {
				const h = s * 0.62;
				return `M${-h} ${-h} L${h} ${-h} L${h} ${h} L${-h} ${h} Z`;
			}
			case 'cross':
				return `M0 ${-s} L0 ${s} M${-s} 0 L${s} 0`;
			case 'pentagon': {
				const pts = Array.from({ length: 5 }, (_, i) => {
					const a = (Math.PI * 2 / 5) * i - Math.PI / 2;
					return `${(s * Math.cos(a)).toFixed(2)} ${(s * Math.sin(a)).toFixed(2)}`;
				});
				return `M${pts.join(' L')} Z`;
			}
			case 'star': {
				const pts = Array.from({ length: 10 }, (_, i) => {
					const a = (Math.PI * 2 / 10) * i - Math.PI / 2;
					const r = i % 2 === 0 ? s : s * 0.4;
					return `${(r * Math.cos(a)).toFixed(2)} ${(r * Math.sin(a)).toFixed(2)}`;
				});
				return `M${pts.join(' L')} Z`;
			}
			case 'octagon': {
				const pts = Array.from({ length: 8 }, (_, i) => {
					const a = (Math.PI * 2 / 8) * i - Math.PI / 2;
					return `${(s * 0.8 * Math.cos(a)).toFixed(2)} ${(s * 0.8 * Math.sin(a)).toFixed(2)}`;
				});
				return `M${pts.join(' L')} Z`;
			}
			case 'circle':
				return `M${s} 0 A${s} ${s} 0 1 1 ${-s} 0 A${s} ${s} 0 1 1 ${s} 0`;
		}
	}

	// -- Animated wave lines --

	const WAVE_COUNT = 10;
	let waveShifts = Array(WAVE_COUNT).fill(0);

	const waveLines = Array.from({ length: WAVE_COUNT }, (_, i) => {
		const y = 60 + i * 85;
		const amp = 12 + Math.random() * 12;
		const opacity = 0.06 + Math.random() * 0.04;
		const path = `M-20 ${y} Q160 ${y - amp} 340 ${y} Q520 ${y + amp} 700 ${y} Q880 ${y - amp} 1060 ${y} Q1240 ${y + amp} 1460 ${y}`;
		return { id: i, path, opacity };
	});

	function nudgeWaves() {
		const count = 1 + Math.floor(Math.random() * 3);
		const next = [...waveShifts];
		for (let j = 0; j < count; j++) {
			const idx = Math.floor(Math.random() * WAVE_COUNT);
			next[idx] = (Math.random() - 0.5) * 100;
		}
		waveShifts = next;
		later(() => {
			waveShifts = waveShifts.map((s) => (Math.random() < 0.35 ? 0 : s));
		}, 4500);
	}

	// -- Ambient viewfinder marks --

	let markId = 0;

	function spawnMark() {
		if (marks.length >= 6) return;
		const m = {
			id: ++markId,
			type: MARK_TYPES[Math.floor(Math.random() * MARK_TYPES.length)],
			x: 6 + Math.random() * 88,
			y: 8 + Math.random() * 84,
			rot: Math.floor(Math.random() * 8) * 45,
			size: 8 + Math.random() * 18,
			life: 4000 + Math.random() * 3000
		};
		marks = [...marks, m];
		later(() => {
			marks = marks.filter((mk) => mk.id !== m.id);
		}, m.life);
	}

	// -- Login modal --

	let loginMode = 'login';
	let email = '';
	let error = '';
	let googleLoading = false;
	let emailLoading = false;
	let magicLinkSent = false;

	function handleContinue() {
		if ($isAuthenticated) {
			navigate('/explorer');
		} else {
			showLogin = true;
			loginMode = 'login';
		}
	}

	function closeLogin() {
		showLogin = false;
		email = '';
		error = '';
		googleLoading = false;
		emailLoading = false;
		magicLinkSent = false;
	}

	function toggleMode() {
		loginMode = loginMode === 'login' ? 'signup' : 'login';
		error = '';
	}

	async function handleGoogleLogin() {
		googleLoading = true;
		error = '';
		try {
			await signInWithGoogle();
			// Auth state change handled by store; $isAuthenticated watcher closes modal
		} catch (err) {
			error = err.message || 'Sign in failed. Please try again.';
			googleLoading = false;
		}
	}

	async function handleSendMagicLink(e) {
		e.preventDefault();
		const cleanEmail = email.trim();
		if (!cleanEmail || !cleanEmail.includes('@')) return;
		emailLoading = true;
		error = '';
		try {
			await sendMagicLink(cleanEmail);
			magicLinkSent = true;
		} catch (err) {
			error = err.message || 'Failed to send sign-in link. Please try again.';
		} finally {
			emailLoading = false;
		}
	}

	// ---------------------------------------------------------------------------
	// MCP handoff: build a hidden form and submit it.
	// ---------------------------------------------------------------------------

	async function performMcpHandoff() {
		if (mcpHandoffSubmitting) return;
		mcpHandoffSubmitting = true;

		const fbUser = window.firebase && window.firebase.auth().currentUser;
		if (!fbUser) {
			error = 'Sign-in succeeded but ID token not available. Please refresh.';
			mcpHandoffSubmitting = false;
			return;
		}

		try {
			const idToken = await fbUser.getIdToken();
			// Hidden form POST — top-level navigation lets the browser follow
			// the 302 redirect (and accept Set-Cookie) cleanly.
			const form = document.createElement('form');
			form.method = 'POST';
			form.action = mcpParams.mcp_complete_url;
			form.style.display = 'none';

			const tokField = document.createElement('input');
			tokField.type = 'hidden';
			tokField.name = 'id_token';
			tokField.value = idToken;
			form.appendChild(tokField);

			const stateField = document.createElement('input');
			stateField.type = 'hidden';
			stateField.name = 'mcp_state';
			stateField.value = mcpParams.mcp_state;
			form.appendChild(stateField);

			document.body.appendChild(form);
			form.submit();
			// Browser navigates away; nothing else to do.
		} catch (err) {
			error = err.message || 'Failed to complete MCP sign-in handoff.';
			mcpHandoffSubmitting = false;
		}
	}

	// Auth watcher is above (runPostLoginSplash)

	// -- Single title element: starts centered, eases to flow position --

	let heroTitleEl;
	let titleInitOffset = { x: 0, y: 0, scale: 1 };
	let titleReady = false;

	// -- Lifecycle --

	// Run splash animation after successful login
	function runPostLoginSplash() {
		phase = 'splash';
		step = -1;

		// Measure title position for centered splash
		requestAnimationFrame(() => {
			if (heroTitleEl) {
				const rect = heroTitleEl.getBoundingClientRect();
				const naturalCX = rect.left + rect.width / 2;
				const naturalCY = rect.top + rect.height / 2;
				const vpCX = window.innerWidth / 2;
				const vpCY = window.innerHeight / 2;
				const splashScale = 1.92;
				titleInitOffset = { x: vpCX - naturalCX, y: vpCY - naturalCY, scale: splashScale };
				titleReady = true;
			}
		});

		markIv = setInterval(spawnMark, 1800);
		later(spawnMark, 300);
		later(spawnMark, 900);

		runSplash();

		// Navigate to explorer after splash completes
		later(() => navigate('/explorer'), 2200);
	}

	// Watch for auth success — branch on MCP mode vs normal jsond-app sign-in
	$: if ($isAuthenticated && (showLogin || mcpMode)) {
		if (mcpMode) {
			// MCP handoff is GATED on explicit user confirmation. The watcher
			// only triggers the handoff once confirmedHandoff is true (set by
			// the user clicking "Continue" on the confirmation card). The card
			// itself is rendered by the markup block below when the user is
			// authenticated AND in mcpMode AND has not yet confirmed.
			if (confirmedHandoff) {
				performMcpHandoff();
			}
		} else {
			closeLogin();
			runPostLoginSplash();
		}
	}

	// Auto-show login modal when arriving in MCP mode. Two cases:
	//   (a) not authenticated → show the sign-in card
	//   (b) authenticated but handoff not yet confirmed → show the
	//       confirmation card with current identity + "use different account"
	$: if (mcpMode && !showLogin && !confirmedHandoff) {
		showLogin = true;
		loginMode = 'login';
	}

	async function handleConfirmHandoff() {
		confirmedHandoff = true;
		// Watcher will see confirmedHandoff = true on next reactive pass and
		// trigger performMcpHandoff(). No direct call here so the state flow
		// stays observable and testable through the reactive system.
	}

	async function handleSwitchAccount() {
		switchingAccount = true;
		try {
			// Clear Firebase session. The mind_session cookie on .adrasi.com
			// is now functionally inert (Mind's authorize Path A retired
			// 2026-05-06) so leaving it to expire naturally in 2h is fine —
			// it has no effect on /authorize, which now always routes
			// through this confirmation flow regardless of cookie state.
			await signOut();
			// onAuthStateChanged fires; $isAuthenticated becomes false. The
			// auto-show watcher re-fires the sign-in card path and the user
			// picks a fresh identity. confirmedHandoff stays false so the
			// next sign-in still requires explicit confirmation.
		} catch (err) {
			error = err.message || 'Failed to sign out. Please try again.';
		} finally {
			switchingAccount = false;
		}
	}

	onMount(async () => {
		document.documentElement.style.background = '#ffffff';
		document.body.style.background = '#ffffff';

		// Gentle ambient waves on hero page (no splash, no marks)
		const waveIv = setInterval(nudgeWaves, 3500 + Math.random() * 3000);
		later(nudgeWaves, 1500);

		// Magic-link callback handling: if the URL is a Firebase
		// signInWithEmailLink target, complete the handshake. After
		// onAuthStateChanged fires, the existing watcher handles the rest.
		try {
			if (isSignInWithEmailLink(window.location.href)) {
				magicLinkCompleting = true;
				try {
					await completeMagicLink(window.location.href);
				} catch (err) {
					if (err.code === 'email-needed') {
						const confirmed = window.prompt(
							'Confirm your email to finish signing in:'
						);
						if (confirmed) {
							await completeMagicLink(window.location.href, confirmed.trim());
						} else {
							magicLinkError = 'Sign-in cancelled.';
						}
					} else {
						magicLinkError = err.message || 'Failed to complete sign-in.';
					}
				} finally {
					magicLinkCompleting = false;
				}
			}
		} catch (err) {
			magicLinkError = err.message || 'Sign-in error.';
		}

		return () => {
			tids.forEach(clearTimeout);
			clearInterval(markIv);
			clearInterval(waveIv);
			document.documentElement.style.background = '';
			document.body.style.background = '';
		};
	});
</script>

<!-- SPLASH LAYER (fixed, outside scroll) -->
<div class="splash-layer" class:phase-transition={phase === 'transition'} class:phase-hero={phase === 'hero'}>
	<div class="splash-bg"></div>
		<!-- Wave overlay -->
		<svg
			class="wave-overlay"
			viewBox="0 0 1440 900"
			preserveAspectRatio="xMidYMid slice"
			aria-hidden="true"
		>
			{#each waveLines as wave (wave.id)}
				<path
					d={wave.path}
					fill="none"
					stroke="rgba(148,163,184,{wave.opacity})"
					stroke-width="0.8"
					style="transform: translateX({waveShifts[wave.id]}px)"
				/>
			{/each}
		</svg>

		<!-- Ambient viewfinder marks -->
		{#each marks as mark (mark.id)}
			<div
				class="mark"
				style="
					left: {mark.x}%;
					top: {mark.y}%;
					--life: {mark.life}ms;
				"
			>
				<svg
					width={mark.size}
					height={mark.size}
					viewBox="0 0 24 24"
					style="transform: rotate({mark.rot}deg)"
				>
					{#if mark.type === 'crosshair'}
						<line x1="12" y1="4" x2="12" y2="20" stroke="currentColor" stroke-width="0.8" />
						<line x1="4" y1="12" x2="20" y2="12" stroke="currentColor" stroke-width="0.8" />
					{:else if mark.type === 'ring'}
						<circle cx="12" cy="12" r="7" fill="none" stroke="currentColor" stroke-width="0.8" />
					{:else if mark.type === 'tick'}
						<line x1="12" y1="6" x2="12" y2="18" stroke="currentColor" stroke-width="0.8" />
					{:else if mark.type === 'point'}
						<circle cx="12" cy="12" r="1.5" fill="currentColor" />
					{/if}
				</svg>
			</div>
		{/each}

		<!-- Shapes (centered during splash) -->
		<div class="content">
			<svg
				class="chain"
				viewBox="0 0 160 36"
				width="200"
				aria-hidden="true"
			>
				<g transform="translate(30, 18)" class="node" class:node-on={step >= 0}>
					<path
						d={shapePath(nodeShapes[0])}
						fill="none"
						stroke-width="1.5"
						stroke-linecap="round"
						stroke-linejoin="round"
					/>
				</g>
				<g transform="translate(80, 18)" class="node" class:node-on={step >= 1}>
					<path
						d={shapePath(nodeShapes[1])}
						fill="none"
						stroke-width="1.5"
						stroke-linecap="round"
						stroke-linejoin="round"
					/>
				</g>
				<g transform="translate(130, 18)" class="node" class:node-on={step >= 2}>
					<path
						d={shapePath(nodeShapes[2])}
						fill="none"
						stroke-width="1.5"
						stroke-linecap="round"
						stroke-linejoin="round"
					/>
				</g>
			</svg>
		</div>
</div>

<!-- SCROLLABLE PAGE -->
<div class="page" class:phase-transition={phase === 'transition'} class:phase-hero={phase === 'hero'}>
	<div class="hero-layer">

		<!-- Section 1: Hero intro (white) -->
		<section class="section-hero">
			<h1
				class="hero-title"
				class:hero-title-splash={phase === 'splash' || (phase === 'transition' && !titleReady)}
				class:hero-title-landed={phase === 'hero'}
				bind:this={heroTitleEl}
				style="--ix: {titleInitOffset.x}px; --iy: {titleInitOffset.y}px; --is: {titleInitOffset.scale}"
			>ADRASI</h1>
			<p class="tagline">LIVING INTELLIGENCE</p>
			<div class="hero-divider"></div>
			<button class="cta" on:click={handleContinue}>CONTINUE</button>
		</section>

		<!-- Section 2: Three pillars (black) -->
		<section class="section-pillars">
			<p class="pillars-headline">Your system mapped, maintained, and reviewed — automatically.</p>
			<div class="pillars">
				<div class="pillar">
					<h3 class="pillar-label">ZERO MANAGEMENT</h3>
					<div class="pillar-rule"></div>
					<p class="pillar-text">No backlogs. No status updates. No maintenance. The system manages itself.</p>
				</div>
				<div class="pillar">
					<h3 class="pillar-label">CONNECT ANYWHERE</h3>
					<div class="pillar-rule"></div>
					<p class="pillar-text">Email. Slack. GitHub. Add a mailbox to your devops subscriptions and stay current, always.</p>
				</div>
				<div class="pillar">
					<h3 class="pillar-label">AUTOMATIC EVALUATION</h3>
					<div class="pillar-rule"></div>
					<p class="pillar-text">Opportunities and risks across your programs, ideas, and research. Surfaced automatically.</p>
				</div>
			</div>
		</section>

		<!-- Section 3: Full name (vertical, centered) -->
		<section class="section-fullname">
			<ul class="fullname-list">
				<li>Autonomous</li>
				<li>Documentation</li>
				<li>Research</li>
				<li>Analysis</li>
				<li>System</li>
				<li>Intelligence</li>
			</ul>
		</section>

		<!-- Footer -->
		<footer class="footer">
			<nav class="footer-links">
				<a href="/about">About</a>
				<a href="/guide">Guide</a>
				<a href="/pricing">Pricing</a>
				<a href="/support">Support</a>
				<a href="/contact">Contact</a>
				<a href="/privacy">Privacy</a>
				<a href="/terms">Terms of Use</a>
			</nav>
			<p class="footer-copy">&copy; 2026 ADRASI</p>
		</footer>

	</div>
</div>

<!-- LOGIN MODAL -->
{#if showLogin}
	<!-- svelte-ignore a11y-click-events-have-key-events -->
	<div class="modal-backdrop" on:click={closeLogin}>
		<!-- svelte-ignore a11y-click-events-have-key-events -->
		<div class="modal" on:click|stopPropagation>
			<button class="modal-back" on:click={closeLogin} aria-label="Close">
				<svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
				</svg>
			</button>

			<div class="modal-body">
				<div class="modal-header">
					<h2 class="modal-title">
						{#if mcpMode && $isAuthenticated && !confirmedHandoff}
							AUTHORIZE MCP
						{:else if mcpMode}
							CONNECT MCP
						{:else}
							{loginMode === 'login' ? 'Welcome Back' : 'Get Started'}
						{/if}
					</h2>
					{#if mcpMode && $isAuthenticated && !confirmedHandoff}
						<p class="modal-subtitle">Confirm the account to authorize</p>
					{:else if mcpMode}
						<p class="modal-subtitle">Sign in to authorize the connection</p>
					{:else}
						<button class="modal-toggle" on:click={toggleMode}>
							{loginMode === 'login'
								? "Don't have an account? Sign up"
								: 'Already have an account? Sign in'}
						</button>
					{/if}
				</div>

				{#if mcpMode && $isAuthenticated && !confirmedHandoff}
					<!--
						Confirmation card. The browser already has a Firebase
						session — the user could be signed in as ANY identity
						that recently authenticated to jsond.ca. Show their
						current identity and require explicit confirmation
						before posting the id_token to Mind. The "use a
						different account" path signs them out and re-shows
						the sign-in card so they can pick a different tenant.
					-->
					<div class="handoff-confirm">
						<div class="handoff-identity">
							<div class="handoff-label">Signed in as</div>
							<div class="handoff-email">{$authState.user?.email || 'unknown account'}</div>
							{#if $authState.tenant?.name}
								<div class="handoff-tenant">{$authState.tenant.name}</div>
							{/if}
						</div>
						<button
							class="modal-submit"
							on:click={handleConfirmHandoff}
							disabled={mcpHandoffSubmitting}
						>
							{#if mcpHandoffSubmitting}
								<svg class="spinner" viewBox="0 0 24 24" fill="none">
									<circle opacity="0.25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
									<path opacity="0.75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
								</svg>
								Authorizing...
							{:else}
								Continue
							{/if}
						</button>
						<button
							class="modal-toggle handoff-switch"
							on:click={handleSwitchAccount}
							disabled={switchingAccount || mcpHandoffSubmitting}
						>
							{#if switchingAccount}
								Signing out...
							{:else}
								Use a different account
							{/if}
						</button>
						{#if error}
							<div class="modal-error">
								<p>{error}</p>
							</div>
						{/if}
					</div>
				{:else if magicLinkSent}
					<div class="magic-sent">
						<div class="magic-sent-title">Check your email</div>
						<p class="magic-sent-body">
							We sent a sign-in link to <strong>{email}</strong>. Click it
							to finish signing in. The link expires in a few minutes.
						</p>
						<button
							class="modal-toggle magic-resend"
							on:click={() => { magicLinkSent = false; }}
						>Use a different email</button>
					</div>
				{:else}
					<form class="modal-form" on:submit={handleSendMagicLink}>
						<div class="input-group input-group-single">
							<input
								type="email"
								bind:value={email}
								placeholder="your@email.com"
								class="modal-input"
								autocomplete="email"
								required
							/>
						</div>
						<button
							type="submit"
							class="modal-submit"
							disabled={!email.trim() || !email.includes('@') || emailLoading}
						>
							{#if emailLoading}
								<svg class="spinner" viewBox="0 0 24 24" fill="none">
									<circle opacity="0.25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
									<path opacity="0.75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
								</svg>
								Sending link...
							{:else}
								Email me a sign-in link
							{/if}
						</button>
					</form>

					<div class="modal-divider">
						<div class="modal-divider-line"></div>
						<span class="modal-divider-text">OR</span>
						<div class="modal-divider-line"></div>
					</div>

					<button
						class="google-btn"
						on:click={handleGoogleLogin}
						disabled={googleLoading || emailLoading}
					>
					{#if googleLoading}
						<svg class="spinner" viewBox="0 0 24 24" fill="none">
							<circle opacity="0.25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
							<path opacity="0.75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
						</svg>
						{loginMode === 'login' ? 'Signing in...' : 'Signing up...'}
					{:else}
						<svg width="20" height="20" viewBox="0 0 24 24">
							<path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
							<path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
							<path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
							<path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
						</svg>
						Continue with Google
					{/if}
				</button>

				{#if error}
					<div class="modal-error">
						<p>{error}</p>
					</div>
				{/if}

					<div class="modal-privacy">
						<p>
							{#if loginMode === 'login'}
								<strong>Welcome back!</strong><br>
								ADRASI is a privacy-first service. Your data is confidential and never shared or trained on.
							{:else}
								<strong>You will be sent a link in email to complete verification.</strong><br>
								Create your account to get started. By signing up you agree to our terms of service. ADRASI is a privacy-first service; your data is confidential and never shared or trained on.
							{/if}
						</p>
					</div>
				{/if}
			</div>
		</div>
	</div>
{/if}

<style>
	/* -- Base -- */

	:global(html), :global(body) {
		margin: 0;
		padding: 0;
		height: 100%;
	}

	.page {
		min-height: 100vh;
		background-color: #ffffff;
	}

	/* -- Splash layer (fixed, independent of scroll) -- */

	.splash-layer {
		position: fixed;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 10;
		pointer-events: none;
	}

	.splash-bg {
		position: absolute;
		inset: 0;
		background-color: #0f172a;
		transition: background-color 0.7s ease;
	}

	.splash-layer.phase-transition .splash-bg,
	.splash-layer.phase-hero .splash-bg {
		background-color: transparent;
	}

	.splash-layer.phase-hero {
		opacity: 0;
		transition: opacity 0.2s ease 0.4s;
	}

	/* -- Waves -- */

	.wave-overlay {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		pointer-events: none;
		transition: opacity 0.8s ease;
	}

	.wave-overlay path {
		transition: transform 4.5s ease-in-out;
	}

	.phase-transition .wave-overlay,
	.phase-hero .wave-overlay {
		opacity: 0;
	}

	/* -- Content (title + shapes) -- */

	.content {
		display: flex;
		flex-direction: column;
		align-items: center;
		z-index: 2;
		margin-top: 160px;
	}

	/* -- Shape sequence -- */

	.chain {
		overflow: visible;
		transition: opacity 1s ease;
	}

	.phase-transition .chain,
	.phase-hero .chain {
		opacity: 0;
	}

	.node path {
		stroke: #9ca3af;
		opacity: 0;
		transform: scale(0.4);
		transform-origin: center;
		transform-box: fill-box;
		transition:
			opacity 0.45s ease,
			transform 0.45s cubic-bezier(0.34, 1.3, 0.64, 1);
	}

	.node-on path {
		opacity: 1;
		transform: scale(1);
	}

	/* -- Ambient marks -- */

	.mark {
		position: absolute;
		color: rgba(255, 255, 255, 0.06);
		pointer-events: none;
		animation: mark-life var(--life, 5000ms) ease-in-out forwards;
		z-index: 1;
	}

	.phase-transition .mark,
	.phase-hero .mark {
		opacity: 0 !important;
		transition: opacity 0.6s ease;
	}

	@keyframes mark-life {
		0% { opacity: 0; }
		12% { opacity: 1; }
		78% { opacity: 1; }
		100% { opacity: 0; }
	}

	/* -- Hero layer (scrollable) -- */

	.section-hero .tagline,
	.section-hero .hero-divider,
	.section-hero .cta,
	.section-pillars,
	.section-fullname,
	.footer {
		opacity: 0;
		pointer-events: none;
		transition: opacity 0.4s ease 0.3s;
	}

	.phase-hero .section-hero .tagline,
	.phase-hero .section-hero .hero-divider,
	.phase-hero .section-hero .cta,
	.phase-hero .section-pillars,
	.phase-hero .section-fullname,
	.phase-hero .footer {
		opacity: 1;
		pointer-events: auto;
	}

	/* -- Section 1: Hero intro (white) -- */

	.section-hero {
		min-height: 100vh;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 120px 24px 80px;
		box-sizing: border-box;
	}

	.hero-title {
		margin: 0 0 32px 0;
		font-family: 'Instrument Sans', sans-serif;
		font-weight: 500;
		font-size: clamp(1.5rem, 4vw, 2.7rem);
		letter-spacing: 0.32em;
		padding-right: 0.32em;
		color: #000000;
		user-select: none;
		position: relative;
		z-index: 20;
		transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1), color 0.7s ease;
	}

	.hero-title-splash {
		transform: translate(var(--ix), var(--iy)) scale(var(--is));
		color: rgba(255, 255, 255, 0.92);
		transition: none;
	}

	.tagline {
		margin: 0;
		font-family: 'Instrument Sans', sans-serif;
		font-weight: 300;
		font-size: clamp(0.7rem, 2vw, 0.85rem);
		letter-spacing: 0.35em;
		color: #000000;
	}

	.hero-divider {
		width: 40px;
		height: 1px;
		background: #d1d5db;
		margin: 28px 0;
	}

	.cta {
		background: #000000;
		color: #ffffff;
		border: none;
		padding: 16px 48px;
		border-radius: 9999px;
		font-family: 'Instrument Sans', sans-serif;
		font-weight: 400;
		font-size: 11px;
		letter-spacing: 0.2em;
		cursor: pointer;
		transition: background-color 0.2s ease;
	}

	.cta:hover {
		background: #1e293b;
	}

	/* -- Section 2: Pillars (black) -- */

	.section-pillars {
		background: #000000;
		padding: 120px 24px;
	}

	.pillars-headline {
		margin: 0 0 64px 0;
		font-family: 'Instrument Sans', sans-serif;
		font-weight: 300;
		font-size: clamp(0.8rem, 2vw, 1rem);
		letter-spacing: 0.15em;
		color: #9CA3AF;
		text-align: center;
	}

	.pillars {
		max-width: 800px;
		margin: 0 auto;
		display: grid;
		grid-template-columns: repeat(3, 140px);
		justify-content: space-between;
		gap: 80px;
	}

	@media (max-width: 768px) {
		.pillars {
			grid-template-columns: 1fr;
			gap: 48px;
			max-width: 400px;
		}
	}

	@media (max-width: 640px) {
		.section-hero { padding: 60px 24px 48px; }
		.section-pillars { padding: 64px 24px; }
		.section-fullname { padding: 32px 24px; }
	}

	.pillar-label {
		margin: 0;
		font-family: 'Instrument Sans', sans-serif;
		font-weight: 400;
		font-size: 11px;
		letter-spacing: 0.25em;
		color: #ffffff;
	}

	.pillar-rule {
		width: 24px;
		height: 1px;
		background: #4b5563;
		margin: 20px 0;
	}

	.pillar-text {
		margin: 0;
		font-family: 'Instrument Sans', sans-serif;
		font-weight: 300;
		font-size: 14px;
		line-height: 1.7;
		color: #9ca3af;
		text-align: left;
	}

	/* -- Section 3: Full name (white, compact) -- */

	.section-fullname {
		padding: 48px 24px;
		display: flex;
		justify-content: center;
	}

	.fullname-list {
		margin: 0;
		padding: 0;
		list-style: none;
		text-align: center;
	}

	.fullname-list li {
		font-family: 'Instrument Sans', sans-serif;
		font-weight: 300;
		font-size: clamp(0.7rem, 1.5vw, 0.9rem);
		letter-spacing: 0.45em;
		text-transform: uppercase;
		color: #D1D5DB;
		line-height: 2.4;
	}

	/* -- Footer -- */

	.footer {
		padding: 48px 24px;
		text-align: center;
		border-top: 1px solid #e5e7eb;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 24px;
	}

	.footer-links {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: 8px 24px;
	}

	.footer-links a {
		font-family: 'Instrument Sans', sans-serif;
		font-weight: 300;
		font-size: 11px;
		letter-spacing: 0.12em;
		text-transform: uppercase;
		color: #6b7280;
		text-decoration: none;
		transition: color 0.2s ease;
	}

	.footer-links a:hover {
		color: #000000;
	}

	.footer-copy {
		margin: 0;
		font-family: 'Instrument Sans', sans-serif;
		font-weight: 300;
		font-size: 11px;
		color: #d1d5db;
		letter-spacing: 0.1em;
	}

	/* -- Login modal -- */

	.modal-backdrop {
		position: fixed;
		inset: 0;
		background: #e5e7eb;
		z-index: 100;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.modal {
		background: #f9fafb;
		border-radius: 24px;
		padding: 32px;
		width: min(340px, 90vw);
		position: relative;
		box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
		font-family: 'Instrument Sans', sans-serif;
		font-weight: 300;
		font-size: 14px;
		color: #000000;
	}

	.modal-back {
		position: absolute;
		top: 24px;
		left: 24px;
		background: none;
		border: none;
		color: #6b7280;
		cursor: pointer;
		padding: 0;
		transition: color 0.2s ease;
	}

	.modal-back:hover {
		color: #000000;
	}

	.modal-body {
		display: flex;
		flex-direction: column;
		gap: 24px;
	}

	.modal-header {
		text-align: center;
	}

	.modal-title {
		margin: 0 0 8px 0;
		font-family: 'Instrument Sans', sans-serif;
		font-weight: 300;
		font-size: 1.25rem;
		letter-spacing: 0.2em;
		text-transform: uppercase;
		color: #000000;
	}

	.modal-toggle {
		background: none;
		border: none;
		font-family: 'Instrument Sans', sans-serif;
		font-size: 12px;
		color: #3b82f6;
		cursor: pointer;
		padding: 0;
		transition: color 0.2s ease;
	}

	.modal-toggle:hover {
		color: #2563eb;
	}

	.modal-subtitle {
		margin: 4px 0 0 0;
		font-family: 'Instrument Sans', sans-serif;
		font-weight: 300;
		font-size: 12px;
		color: #6b7280;
		letter-spacing: 0.05em;
	}

	.input-group-single {
		/* Single-input variant — no internal divider needed. */
	}

	.magic-sent {
		display: flex;
		flex-direction: column;
		gap: 12px;
		padding: 4px 0 8px;
		text-align: center;
	}

	.magic-sent-title {
		font-family: 'Instrument Sans', sans-serif;
		font-weight: 400;
		font-size: 14px;
		letter-spacing: 0.18em;
		text-transform: uppercase;
		color: #000000;
	}

	.magic-sent-body {
		margin: 0;
		font-family: 'Instrument Sans', sans-serif;
		font-weight: 300;
		font-size: 13px;
		line-height: 1.6;
		color: #4b5563;
	}

	.magic-sent-body strong {
		font-weight: 500;
		color: #000000;
	}

	.magic-resend {
		align-self: center;
	}

	.handoff-confirm {
		display: flex;
		flex-direction: column;
		gap: 16px;
		padding: 4px 0 8px;
	}

	.handoff-identity {
		display: flex;
		flex-direction: column;
		gap: 4px;
		text-align: center;
		padding: 16px;
		background: #f3f4f6;
		border-radius: 8px;
	}

	.handoff-label {
		font-family: 'Instrument Sans', sans-serif;
		font-weight: 300;
		font-size: 11px;
		letter-spacing: 0.18em;
		text-transform: uppercase;
		color: #6b7280;
	}

	.handoff-email {
		font-family: 'Instrument Sans', sans-serif;
		font-weight: 500;
		font-size: 15px;
		color: #000000;
		word-break: break-word;
	}

	.handoff-tenant {
		font-family: 'Instrument Sans', sans-serif;
		font-weight: 300;
		font-size: 12px;
		color: #6b7280;
		margin-top: 2px;
	}

	.handoff-switch {
		align-self: center;
	}

	.modal-form {
		display: flex;
		flex-direction: column;
		gap: 16px;
	}

	.input-group {
		background: #cffafe;
		border-radius: 8px;
		overflow: hidden;
	}

	.modal-input {
		width: 100%;
		padding: 12px;
		background: transparent;
		border: none;
		font-family: 'Instrument Sans', sans-serif;
		font-weight: 300;
		font-size: 14px;
		color: #111827;
		outline: none;
		box-sizing: border-box;
	}

	.modal-input::placeholder {
		color: #9ca3af;
	}

	.input-divider {
		height: 0;
		border-top: 1px dashed #99e0eb;
		margin: 0 12px;
	}

	.modal-submit {
		width: 100%;
		padding: 16px 24px;
		background: #000000;
		color: #ffffff;
		border: none;
		border-radius: 9999px;
		font-family: 'Instrument Sans', sans-serif;
		font-weight: 300;
		font-size: 11px;
		letter-spacing: 0.2em;
		text-transform: uppercase;
		cursor: pointer;
		transition: opacity 0.2s ease;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
	}

	.modal-submit:hover {
		opacity: 0.8;
	}

	.modal-submit:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.modal-divider {
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.modal-divider-line {
		flex: 1;
		height: 1px;
		background: #d1d5db;
	}

	.modal-divider-text {
		font-size: 11px;
		color: #9ca3af;
		letter-spacing: 0.2em;
		text-transform: uppercase;
	}

	.google-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 12px;
		width: 100%;
		padding: 12px 24px;
		background: #ffffff;
		border: 1px solid #94a3b8;
		border-radius: 9999px;
		font-family: 'Instrument Sans', sans-serif;
		font-weight: 300;
		font-size: 11px;
		letter-spacing: 0.2em;
		text-transform: uppercase;
		color: #000000;
		cursor: pointer;
		transition: background-color 0.2s ease;
	}

	.google-btn:hover {
		background: #f9fafb;
	}

	.google-btn:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.modal-error {
		background: #fef2f2;
		border: 1px solid #fecaca;
		border-radius: 8px;
		padding: 12px;
	}

	.modal-error p {
		margin: 0;
		font-size: 12px;
		color: #dc2626;
		text-align: center;
	}

	.modal-privacy {
		background: #f3f4f6;
		border-radius: 8px;
		padding: 16px;
	}

	.modal-privacy p {
		margin: 0;
		font-size: 12px;
		color: #4b5563;
		line-height: 1.6;
		text-align: left;
	}

	.modal-privacy strong {
		font-weight: 600;
	}

	.spinner {
		width: 16px;
		height: 16px;
		animation: spin 1s linear infinite;
	}

	@keyframes spin {
		from { transform: rotate(0deg); }
		to { transform: rotate(360deg); }
	}
</style>
